import React, {createRef, ReactNode, RefObject} from "react";
import {Dialog} from "primereact/dialog";
import {Button} from "primereact/button";
import AdminDialog, {AdminDialogProperties, AdminDialogState} from "./AdminDialog";
import AdminDialogType from "../AdminDialogType";
import Message from "../../../shared/log/Message";
import {MessageType} from "../../../shared/log/MessageInterface";
import Administration from "../../session/Administration";
import {
	Application,
	ApplicationConfigProxy,
	ApplicationConfigProxyInterface,
	ConfigurationType,
	ProxiesApplication
} from "@softvision/webpdf-wsclient-9-typescript/lib/generated-sources";
import ProxyDialogFields from "../field/ProxyDialogFields";
import {TabPanel, TabView} from "primereact/tabview";
import {InputSwitch} from "primereact/inputswitch";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import Translate from "../../../shared/translation/Translate";
import Helper from "../../../shared/components/Helper";
import Icon from "../../../shared/components/Icon";
import Info, {InfoType} from "../../../shared/components/Info";
import FunctionHelper from "../../../shared/helper/FunctionHelper";
import DomHelper from "../../../shared/helper/DomHelper";
import {Password} from "primereact/password";

interface ProxyDialogStatus extends AdminDialogState<ProxiesApplication> {
	httpPasswordChanged: boolean
	httpsPasswordChanged: boolean
	panel: number;
}

export class ProxyDialogDefinition extends AdminDialog<ProxiesApplication, AdminDialogProperties<ProxiesApplication>,
	ProxyDialogStatus> {
	private httpPasswordField: RefObject<Password>;
	private httpsPasswordField: RefObject<Password>;

	constructor(props: Readonly<AdminDialogProperties<ProxiesApplication>>) {
		super(props);

		this.state = {
			...this.state,
			panel: 0,
			httpPasswordChanged: false,
			httpsPasswordChanged: false
		};

		this.httpPasswordField = createRef<Password>();
		this.httpsPasswordField = createRef<Password>();

		this.onChange = this.onChange.bind(this);
	}

	private renderHttpTab(parameter?: ProxiesApplication): ReactNode {
		let proxies: Array<ApplicationConfigProxy> = FunctionHelper.applyValue(
			parameter?.proxy, ProxiesApplication.getProxyDefault()
		);
		let httpParameter: ApplicationConfigProxy | undefined;

		for (let proxy of proxies) {
			if (proxy.scheme === "http") {
				httpParameter = proxy;
				break;
			}
		}

		return (
			<fieldset className="p-col-12 p-grid">
				<div className=" p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-http-enabled">
						<Translate {...this.props} data="proxy.http.enabled" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-8 switch-container">
						<InputSwitch inputId="control-proxy-http-enabled" checked={FunctionHelper.exists(httpParameter)}
									 onChange={(e) => this.onChange(
										 ProxyDialogFields.HTTP_ENABLED, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!FunctionHelper.exists(httpParameter)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-http-host">
						<Translate {...this.props} data="proxy.http.host" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigProxy.getHostDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-proxy-http-host"
								   value={FunctionHelper.applyValue(
									   httpParameter?.host, ApplicationConfigProxy.getHostDefault()
								   )}
								   onInput={(e) => this.onChange(
									   ProxyDialogFields.HTTP_HOST, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!FunctionHelper.exists(httpParameter)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-http-port">
						<Translate {...this.props} data="proxy.http.port" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigProxy.getPortDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputNumber inputId="control-proxy-http-port" step={1} showButtons={true} useGrouping={false}
									 value={FunctionHelper.applyValue(
										 httpParameter?.port, ApplicationConfigProxy.getPortDefault()
									 )}
									 min={ApplicationConfigProxy?.getPortMin()}
									 onChange={(e) => this.onChange(
										 ProxyDialogFields.HTTP_PORT, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!FunctionHelper.exists(httpParameter)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-http-user">
						<Translate {...this.props} data="proxy.http.user" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigProxy.getUserNameDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-proxy-http-user"
								   value={FunctionHelper.applyValue(
									   httpParameter?.userName, ApplicationConfigProxy.getUserNameDefault()
								   )}
								   onInput={(e) => this.onChange(
									   ProxyDialogFields.HTTP_USER, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!FunctionHelper.exists(httpParameter)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-http-password">
						<Translate {...this.props} data="proxy.http.password" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigProxy.getPasswordDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<Password id="control-proxy-http-password" feedback={false} ref={this.httpPasswordField}
								  appendTo={document.getElementById("root")}
								  toggleMask={this.state.httpPasswordChanged}
								  onFocus={() => {
									  if (this.state.httpPasswordChanged) {
										  return;
									  }

									  this.onChange(ProxyDialogFields.HTTP_PASSWORD, "");

									  this.setState({
										  httpPasswordChanged: true
									  });
								  }}
								  value={FunctionHelper.applyValue(
									  httpParameter?.password, ApplicationConfigProxy.getPasswordDefault()
								  )}
								  onInput={(e) => this.onChange(
									  ProxyDialogFields.HTTP_PASSWORD, e.currentTarget.value
								  )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	private renderHttpsTab(parameter?: ProxiesApplication): ReactNode {
		let proxies: Array<ApplicationConfigProxy> = FunctionHelper.applyValue(
			parameter?.proxy, ProxiesApplication.getProxyDefault()
		);
		let httpsParameter: ApplicationConfigProxy | undefined;

		for (let proxy of proxies) {
			if (proxy.scheme === "https") {
				httpsParameter = proxy;
				break;
			}
		}

		return (
			<fieldset className="p-col-12 p-grid">
				<div className=" p-col-12 p-grid">
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-https-enabled">
						<Translate {...this.props} data="proxy.https.enabled" namespace="dialog"/>
					</label>
					<div className="p-col-12 p-sm-8 switch-container">
						<InputSwitch inputId="control-proxy-https-enabled"
									 checked={FunctionHelper.exists(httpsParameter)}
									 onChange={(e) => this.onChange(
										 ProxyDialogFields.HTTPS_ENABLED, e.value
									 )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!FunctionHelper.exists(httpsParameter)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-https-host">
						<Translate {...this.props} data="proxy.https.host" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigProxy.getHostDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-proxy-https-host"
								   value={FunctionHelper.applyValue(
									   httpsParameter?.host, ApplicationConfigProxy.getHostDefault()
								   )}
								   onInput={(e) => this.onChange(
									   ProxyDialogFields.HTTPS_HOST, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!FunctionHelper.exists(httpsParameter)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-https-port">
						<Translate {...this.props} data="proxy.https.port" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigProxy.getPortDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8">
						<InputNumber inputId="control-proxy-https-port" step={1} showButtons={true} useGrouping={false}
									 value={FunctionHelper.applyValue(
										 httpsParameter?.port, ApplicationConfigProxy.getPortDefault()
									 )}
									 min={ApplicationConfigProxy.getPortMin()}
									 onChange={(e) => this.onChange(
										 ProxyDialogFields.HTTPS_PORT, e.value)}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!FunctionHelper.exists(httpsParameter)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-https-user">
						<Translate {...this.props} data="proxy.https.user" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigProxy.getUserNameDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<InputText id="control-proxy-https-user"
								   value={FunctionHelper.applyValue(
									   httpsParameter?.userName, ApplicationConfigProxy.getUserNameDefault()
								   )}
								   onInput={(e) => this.onChange(
									   ProxyDialogFields.HTTPS_USER, e.currentTarget.value
								   )}/>
					</div>
				</div>
				<div className="p-col-12 p-grid" hidden={!FunctionHelper.exists(httpsParameter)}>
					<label className="p-col-12 p-sm-4" htmlFor="control-proxy-https-password">
						<Translate {...this.props} data="proxy.https.password" namespace="dialog"/>
						<Info {...this.props} type={InfoType.Help} markdown={true}
							  data={ApplicationConfigProxy.getPasswordDescription()}/>
					</label>
					<div className="p-col-12 p-sm-8 p-grid">
						<Password id="control-proxy-https-password" feedback={false} ref={this.httpsPasswordField}
								  appendTo={document.getElementById("root")}
								  toggleMask={this.state.httpsPasswordChanged}
								  onFocus={() => {
									  if (this.state.httpsPasswordChanged) {
										  return;
									  }

									  this.onChange(ProxyDialogFields.HTTPS_PASSWORD, "");

									  this.setState({
										  httpsPasswordChanged: true
									  });
								  }}
								  value={FunctionHelper.applyValue(
									  httpsParameter?.password, ApplicationConfigProxy.getPasswordDefault()
								  )}
								  onInput={(e) => this.onChange(
									  ProxyDialogFields.HTTPS_PASSWORD, e.currentTarget.value
								  )}/>
					</div>
				</div>
			</fieldset>
		);
	}

	public renderContents(): ReactNode {
		let parameter: ProxiesApplication | undefined = this.state.parameter;

		return (
			<TabView activeIndex={this.state.panel} onTabChange={
				(e) => this.setState({panel: e.index})
			}>
				<TabPanel header={Translate.value("proxy.http.label", {ns: "dialog"})}>
					{this.renderHttpTab(parameter)}
				</TabPanel>
				<TabPanel header={Translate.value("proxy.https.label", {ns: "dialog"})}>
					{this.renderHttpsTab(parameter)}
				</TabPanel>
			</TabView>
		);
	}

	public renderDialog(): ReactNode {
		let classList: Array<string> = ["proxy-dialog"];
		if (this.state.submitted) {
			classList.push("submitted");
		}

		return (
			<Dialog className={classList.join(" ")} visible={this.props.visible} onHide={this.hide}
					closeOnEscape={true} header={this.renderHeader()} footer={this.renderFooter()} resizable={false}
					appendTo={document.getElementById("root")}>
				<div className="p-grid">
					{this.renderError()}
					<Helper {...this.props}>
						<Translate {...this.props} data={"dialog.admin.proxy.general"} namespace={"help"}
								   markdown={true}/>
					</Helper>
					<Helper {...this.props} type={MessageType.WARNING}>
						<Translate {...this.props} data={"dialog.admin.proxy.info"} namespace={"help"} markdown={true}/>
					</Helper>
					{this.renderContents()}
				</div>
			</Dialog>
		);
	}

	protected renderHeader(): ReactNode {
		return (
			<div>
				<h1>
					<Icon icon={"icon-proxy"}/>
					<Translate {...this.props} data="proxy.title" namespace="dialog"/>
				</h1>
				<Button icon="icon icon-close" onClick={() => this.hide()}/>
				{this.renderHelpButton()}
			</div>
		);
	}

	protected isValid(): boolean {
		let parameter: ProxiesApplication = FunctionHelper.applyValue(
			this.state.parameter, new ProxiesApplication({})
		);

		if (!FunctionHelper.exists(parameter)) {
			return false;
		}

		let result: boolean = super.isValid();

		let proxies: Array<ApplicationConfigProxy> = FunctionHelper.applyValue(
			parameter?.proxy, ProxiesApplication.getProxyDefault()
		);

		for (let proxy of proxies) {
			if ((FunctionHelper.isEmpty(proxy?.password) && !FunctionHelper.isEmpty(proxy?.userName)) ||
				(!FunctionHelper.isEmpty(proxy?.password) && FunctionHelper.isEmpty(proxy?.userName))) {
				result = false;
			}
		}

		return result;
	}

	protected onChange(field: ProxyDialogFields, value: any): void {
		let parameter: ProxiesApplication = FunctionHelper.applyValue(
			this.state.parameter, new ProxiesApplication({})
		);
		let proxies: Array<ApplicationConfigProxy> = FunctionHelper.applyValue(
			parameter?.proxy, ProxiesApplication.getProxyDefault()
		);
		let httpsParameter: ApplicationConfigProxy | undefined;
		let httpParameter: ApplicationConfigProxy | undefined;

		for (let proxy of proxies) {
			if (proxy.scheme === "http") {
				httpParameter = proxy;
			} else if (proxy.scheme === "https") {
				httpsParameter = proxy;
			}
		}

		switch (field) {
			case ProxyDialogFields.HTTPS_ENABLED:
				if (value) {
					proxies.push(ApplicationConfigProxy.fromJson({
						scheme: "https",
						host: "",
						port: 8443,
						userName: "",
						password: ""
					} as ApplicationConfigProxyInterface));
				} else {
					for (let [index, proxy] of proxies.entries()) {
						if (proxy.scheme === "https") {
							proxies.splice(index, 1);
						}
					}
				}

				parameter!.proxy = proxies;
				break;
			case ProxyDialogFields.HTTPS_HOST:
				httpsParameter!.host = value;
				break;
			case ProxyDialogFields.HTTPS_PORT:
				httpsParameter!.port = value;
				break;
			case ProxyDialogFields.HTTPS_USER:
				httpsParameter!.userName = value;
				break;
			case ProxyDialogFields.HTTPS_PASSWORD:
				httpsParameter!.password = value;
				break;
			case ProxyDialogFields.HTTP_ENABLED:
				if (value) {
					proxies.push(ApplicationConfigProxy.fromJson({
						scheme: "http",
						host: "",
						port: 8080,
						userName: "",
						password: ""
					} as ApplicationConfigProxyInterface));
				} else {
					for (let [index, proxy] of proxies.entries()) {
						if (proxy.scheme === "http") {
							proxies.splice(index, 1);
						}
					}
				}

				parameter!.proxy = proxies;
				break;
			case ProxyDialogFields.HTTP_HOST:
				httpParameter!.host = value;
				break;
			case ProxyDialogFields.HTTP_PORT:
				httpParameter!.port = value;
				break;
			case ProxyDialogFields.HTTP_USER:
				httpParameter!.userName = value;
				break;
			case ProxyDialogFields.HTTP_PASSWORD:
				httpParameter!.password = value;
				break;
		}

		this.setState({
			parameter: parameter
		}, () => {
			super.onChange(field, value);
		});
	}

	protected async submit(): Promise<void> {
		await super.submit();

		let administration: Administration = this.props.context.session.getAdministration();
		let applicationConfiguration: Application = (
			await administration.getConfiguration(ConfigurationType.Application) as Application
		).clone();
		applicationConfiguration.proxies = this.state.parameter;

		try {
			await administration.updateConfiguration(ConfigurationType.Application, applicationConfiguration);

			this.setState({
				hasChanges: false
			});
		} catch (exception: any) {
			if (await this.onError(exception)) {
				await this.submit();
			}
			return;
		}

		let message: Message = new Message("success.administration.proxy", MessageType.SUCCESS);
		this.props.context.getPage().addMessage(message);
		this.props.context.getPage().setState({
			showRestartMessage: true
		});
		this.hide();
	}

	protected getDialogType(): AdminDialogType {
		return AdminDialogType.PROXY;
	}

	protected async reset(): Promise<void> {
		await super.reset();

		for (let passwordField of [this.httpPasswordField.current, this.httpsPasswordField.current]) {
			if (!FunctionHelper.exists(passwordField)) {
				continue;
			}

			let passwordContainer: HTMLDivElement = passwordField!.getElement();
			let type: string | null | undefined = DomHelper.find(passwordContainer, "input")?.getAttribute("type");

			if (type !== "password") {
				DomHelper.find(passwordContainer, "input")?.setAttribute("type", "password");
				DomHelper.find(passwordContainer, "i")?.setAttribute("class", "pi pi-eye");
			}
		}

		this.setState({
			httpPasswordChanged: false,
			httpsPasswordChanged: false
		});
	}

	protected hide() {
		this.setState({
			httpPasswordChanged: false,
			httpsPasswordChanged: false
		});

		super.hide();
	}
}

const ProxyDialog = React.memo(ProxyDialogDefinition,
	(
		prevProps: Readonly<AdminDialogProperties<ProxiesApplication>>,
		nextProps: Readonly<AdminDialogProperties<ProxiesApplication>>
	) => {
		if (!nextProps.visible && !prevProps.visible) {
			return true
		}

		return false;
	}
);

export default ProxyDialog;